import { Color, ZoneType } from "@fairfleet/geotab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { colorToCss, cssToColor, FALLBACK_ZONE_COLOR } from "../utils/zoneColor";

interface Props {
  /** Undefined = create a new type. */
  type?: ZoneType;
  busy: boolean;
  onSave(values: { name: string; comment: string; fillColor: Color }): void;
  onClose(): void;
}

/** Mount with a `key` per type so the fields start from that type. */
export default function ZoneTypeDialog({ type, busy, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(type?.name ?? "");
  const [comment, setComment] = useState(type?.comment ?? "");
  const [fillColor, setFillColor] = useState<Color>(type?.fillColor ?? FALLBACK_ZONE_COLOR);
  const nameMissing = name.trim() === "";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{type ? t("zonesPage.types.editTitle") : t("zonesPage.types.createTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t("zonesPage.form.name")}
            value={name}
            onChange={e => setName(e.target.value)}
            required
            autoFocus
            size="small"
          />
          <TextField
            label={t("zonesPage.form.comment")}
            value={comment}
            onChange={e => setComment(e.target.value)}
            size="small"
            multiline
          />
          <TextField
            label={t("zonesPage.form.color")}
            type="color"
            value={colorToCss(fillColor)}
            onChange={e => setFillColor(cssToColor(e.target.value, fillColor.a))}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("zonesPage.cancel")}</Button>
        <Button
          variant="contained"
          disabled={nameMissing || busy}
          onClick={() => onSave({ name, comment, fillColor })}
        >
          {t("zonesPage.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
